// The schedule's arithmetic (F520, decision 128): the day in UTC, the
// quarter hours, the free stretches between what is busy, the countdown.
// Pure functions, tested on their own; the pages draw from them.

/** A slot's length in minutes: nobody chooses one. */
export const SLOT_MIN = 30;
/** A slot is booked at least this many minutes ahead. */
export const NOTICE_MIN = 60;
/** And at most this many days ahead. */
export const HORIZON_DAYS = 14;

export type Span = { start: Date; end: Date };

export function addMinutes(d: Date, m: number): Date {
  return new Date(d.getTime() + m * 60_000);
}

/** 00:00 UTC of the day `d` falls on. */
export function dayStartUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function snapDownToQuarter(d: Date): Date {
  const t = new Date(d.getTime());
  t.setUTCSeconds(0, 0);
  t.setUTCMinutes(t.getUTCMinutes() - (t.getUTCMinutes() % 15));
  return t;
}

export function snapUpToQuarter(d: Date): Date {
  const down = snapDownToQuarter(d);
  return down.getTime() === d.getTime() ? down : addMinutes(down, 15);
}

/** The first quarter hour a slot can start at: an hour from now, rounded up. */
export function firstBookable(now: Date): Date {
  return snapUpToQuarter(addMinutes(now, NOTICE_MIN));
}

/** The last quarter hour a slot can start at: fourteen days from now, rounded down. */
export function lastBookable(now: Date): Date {
  return snapDownToQuarter(addMinutes(now, HORIZON_DAYS * 24 * 60));
}

/** "20:00", UTC. */
export function utcClock(d: Date): string {
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

/** "21:00", on this PC's clock. */
export function localClockOf(d: Date): string {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Sat 31 Oct", the UTC date. */
export function utcDayLabel(d: Date): string {
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** The position of a moment on a day's lane, 0 to 100, clamped. */
export function pct(d: Date, dayStart: Date): number {
  const f = (d.getTime() - dayStart.getTime()) / 86_400_000;
  return Math.max(0, Math.min(100, f * 100));
}

/** The stretches of `from` to `to` that no busy span touches and that hold a
 * slot, starting no earlier than `earliest` (rounded up to the quarter). */
export function freeStretches(busy: Span[], from: Date, to: Date, earliest: Date): Span[] {
  const sorted = [...busy].filter((b) => b.end > from && b.start < to).sort((a, b) => a.start.getTime() - b.start.getTime());
  const out: Span[] = [];
  let cursor = new Date(Math.max(from.getTime(), snapUpToQuarter(earliest).getTime()));
  for (const b of sorted) {
    if (b.start > cursor) out.push({ start: cursor, end: b.start });
    if (b.end > cursor) cursor = snapUpToQuarter(b.end);
  }
  if (to > cursor) out.push({ start: cursor, end: to });
  return out.filter((s) => s.end.getTime() - s.start.getTime() >= SLOT_MIN * 60_000);
}

/** The quarter hour a click lands on inside a free stretch, so that the half
 * hour fits in it; `fraction` is where along the stretch the click fell. */
export function slotAtClick(stretch: Span, fraction: number): Date {
  const f = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
  const at = snapDownToQuarter(new Date(stretch.start.getTime() + f * (stretch.end.getTime() - stretch.start.getTime())));
  const latest = snapDownToQuarter(addMinutes(stretch.end, -SLOT_MIN));
  const t = new Date(Math.min(Math.max(at.getTime(), stretch.start.getTime()), latest.getTime()));
  return t;
}

/** "24:10", the minutes and seconds left; "0:00" once past. */
export function countdown(endsAt: Date, now: Date): string {
  const s = Math.max(0, Math.floor((endsAt.getTime() - now.getTime()) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

/** "1 h 50", "24 min", "2 d 3 h": how far a moment is, for a chip. */
export function inWords(at: Date, now: Date): string {
  const min = Math.max(0, Math.round((at.getTime() - now.getTime()) / 60_000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${String(min % 60).padStart(2, "0")}`;
  const d = Math.floor(h / 24);
  return `${d} d ${h % 24} h`;
}
